export type BeneficiaryPaymentRail = 'sepa' | 'swift';

export type BeneficiaryCountrySupport =
  | { status: 'supported'; paymentRail: 'sepa' }
  | { status: 'swift-required'; paymentRail: 'swift' }
  | { status: 'unsupported' };

export type BeneficiaryCountrySupportOptions = {
  isSwiftEnabled: boolean;
};

export type BeneficiaryCountrySupportMessage = {
  severity: 'warn' | 'info';
  messageKey: string;
};

export type BeneficiaryCountrySupportView = {
  isSupported: boolean;
  paymentRail: BeneficiaryPaymentRail | null;
  message: BeneficiaryCountrySupportMessage | null;
};

export const SEPA_COUNTRY_CODES: readonly string[] = [
  'AD',
  'AL',
  'AT',
  'BE',
  'BG',
  'CH',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GB',
  'GI',
  'GR',
  'HR',
  'HU',
  'IE',
  'IS',
  'IT',
  'LI',
  'LT',
  'LU',
  'LV',
  'MC',
  'MD',
  'ME',
  'MK',
  'MT',
  'NL',
  'NO',
  'PL',
  'PT',
  'RO',
  'RS',
  'SE',
  'SI',
  'SK',
  'SM',
  'VA',
];

const SEPA_COUNTRY_CODE_SET: ReadonlySet<string> = new Set(SEPA_COUNTRY_CODES);

export const resolveBeneficiaryCountrySupport = (
  countryCode: string | null | undefined,
  { isSwiftEnabled }: BeneficiaryCountrySupportOptions,
): BeneficiaryCountrySupport | null => {
  const normalizedCountryCode = countryCode?.trim().toUpperCase();

  if (normalizedCountryCode === undefined || normalizedCountryCode.length === 0) {
    return null;
  }

  if (SEPA_COUNTRY_CODE_SET.has(normalizedCountryCode)) {
    return { status: 'supported', paymentRail: 'sepa' };
  }

  return isSwiftEnabled
    ? { status: 'swift-required', paymentRail: 'swift' }
    : { status: 'unsupported' };
};

export const toBeneficiaryCountrySupportView = (
  countryCode: string | null | undefined,
  options: BeneficiaryCountrySupportOptions,
): BeneficiaryCountrySupportView => {
  const support = resolveBeneficiaryCountrySupport(countryCode, options);

  if (support === null) {
    return { isSupported: true, paymentRail: null, message: null };
  }

  if (support.status === 'unsupported') {
    return {
      isSupported: false,
      paymentRail: null,
      message: {
        severity: 'warn',
        messageKey: 'beneficiary_bank_form.country_support.unsupported',
      },
    };
  }

  if (support.status === 'swift-required') {
    return {
      isSupported: true,
      paymentRail: support.paymentRail,
      message: {
        severity: 'info',
        messageKey: 'beneficiary_bank_form.country_support.swift_required',
      },
    };
  }

  return { isSupported: true, paymentRail: support.paymentRail, message: null };
};
